import React, { useState, useEffect } from 'react';
import axios from 'axios';
import NewsCard from '../components/NewsCard';
import CategoryFilter from '../components/CategoryFilter';
import UserStats from '../components/UserStats';
import BadgesShowcase from '../components/BadgesShowcase';
import RecommendedSection from '../components/RecommendedSection';
import SubscriptionBanner from '../components/SubscriptionBanner';
import SubscriptionModal from '../components/SubscriptionModal';
import PhotoOfTheWeek from '../components/PhotoOfTheWeek';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Loader2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const { getAuthHeader, user, isAdmin } = useAuth();
  const { t } = useLanguage();
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);

  const fetchNews = async (category = 'all') => {
    try {
      setLoading(true);
      const params = category !== 'all' ? { category } : {};
      const response = await axios.get(`${API}/news`, {
        params,
        headers: getAuthHeader()
      });
      setNews(response.data);
    } catch (error) {
      console.error('Error fetching news:', error);
      toast.error('Failed to load news');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNews(selectedCategory);
  }, [selectedCategory]);

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
  };

  const handleBookmarkToggle = async (newsId, isBookmarked) => {
    try {
      if (isBookmarked) {
        await axios.delete(`${API}/bookmarks/${newsId}`, {
          headers: getAuthHeader()
        });
        toast.success('Bookmark removed');
      } else {
        await axios.post(`${API}/bookmarks?news_id=${newsId}`, {}, {
          headers: getAuthHeader()
        });
        toast.success('Bookmark added');
      }
      fetchNews(selectedCategory);
    } catch (error) {
      toast.error('Failed to update bookmark');
    }
  };

  const handleDeleteArticle = async (newsId) => {
    try {
      await axios.delete(`${API}/admin/news/${newsId}`, {
        headers: getAuthHeader()
      });
      toast.success('Article deleted');
      fetchNews(selectedCategory);
    } catch (error) {
      toast.error('Failed to delete article');
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFDF5] dark:bg-[#1a1816]">
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Sparkles className="w-8 h-8 text-[#FF6B6B]" strokeWidth={2.5} />
            <h1 className="text-5xl md:text-7xl font-bold dark:text-white" style={{ fontFamily: 'Merriweather, serif' }}>
              {t('home.title')}
            </h1>
            <Sparkles className="w-8 h-8 text-[#4ECDC4]" strokeWidth={2.5} />
          </div>
          <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            {t('home.subtitle')}
          </p>
        </div>

        {user && (
          <div className="mb-8 space-y-8">
            <UserStats />
            <BadgesShowcase />
          </div>
        )}

        {user && (
          <SubscriptionBanner onUpgrade={() => setShowSubscriptionModal(true)} />
        )}

        {/* Photo of the Week - shows on Fridays */}
        <PhotoOfTheWeek />

        {/* Recommended for You */}
        <RecommendedSection />

        <CategoryFilter
          selectedCategory={selectedCategory}
          onCategoryChange={handleCategoryChange}
        />

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-[#FF6B6B]" strokeWidth={2.5} />
          </div>
        ) : news.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-xl text-gray-600 dark:text-gray-400" data-testid="no-news-message">
              {t('home.noNews')}
            </p>
          </div>
        ) : (
          <div className="max-w-2xl mx-auto" data-testid="news-grid">
            {news.map((item) => (
              <NewsCard
                key={item.id}
                news={item}
                onBookmarkToggle={handleBookmarkToggle}
                onDelete={handleDeleteArticle}
                isAdmin={isAdmin}
              />
            ))}
          </div>
        )}
      </div>
      {showSubscriptionModal && (
        <SubscriptionModal onClose={() => setShowSubscriptionModal(false)} />
      )}
    </div>
  );
};

export default Home;